import { Directive, ElementRef, Renderer2, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from '../../environments/environment';

@Directive({
  selector: '[appAddDocumentationLink]',
  standalone: true,
})
export class AddDocumentationLinkDirective implements OnInit, OnDestroy, AfterViewInit {
  private compodocBaseUrl: string = 'http://127.0.0.1:8080'; // Base URL for Compodoc documentation
  private observer: MutationObserver;
  private routerSubscription: Subscription;
  private banner: HTMLElement;
  private bannerTimeout: any;
  private countdown: number = 5; // 5-second countdown
  private countdownInterval: any;
  private hoverDelayTimeout: any;
  private activeElement: HTMLElement | null = null; // Active element to apply effects
  private originalStyles: Map<HTMLElement, { [key: string]: string }> = new Map(); // Map to store original styles

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private router: Router
  ) {}

  ngOnInit() {
    // Check if the enableScanDOMMode mode is enabled in the environment settings
    if (!environment.enableScanDOMMode) {
      return; // If not enabled, do nothing
    }

    // Subscribe to router events to rescan the DOM when the route changes
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // Skip processing if the current route includes 'auth/redirect'
        if (this.router.url.includes('auth/redirect')) {
          return;
        }
        this.scanDOM(); // Rescan the DOM on navigation end
      }
    });
  }

  ngAfterViewInit() {
    // Check enableScanDOMMode environment settings again
    if (!environment.enableScanDOMMode) {
      return;
    } else {
      // Perform an initial scan of the DOM after the view is fully initialized
      if (!this.router.url.includes('auth/redirect')) {
        this.scanDOM();
      }
    }
  }

  ngOnDestroy() {
    this.cleanUp(); // Cleanup resources when the directive is destroyed
  }

  private cleanUp() {
    // Disconnect the observer if it exists
    if (this.observer) {
      this.observer.disconnect();
    }
    // Unsubscribe from the router events
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    // Remove the banner element if it exists
    if (this.banner) {
      this.renderer.removeChild(document.body, this.banner);
    }
    // Clear any running timeouts or intervals
    if (this.bannerTimeout) {
      clearTimeout(this.bannerTimeout);
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    if (this.hoverDelayTimeout) {
      clearTimeout(this.hoverDelayTimeout);
    }
  }

  private scanDOM() {
    // Disconnect the observer before starting a new scan
    if (this.observer) {
      this.observer.disconnect();
    }

    // Create and configure a MutationObserver to monitor the document body
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        Array.from(mutation.addedNodes).forEach((node: Node) => {
          if (node.nodeType === 1) {
            // NodeType 1 is for HTML elements
            const element = node as HTMLElement;
            this.processElement(element); // Process the node and its children
          }
        });
      });
    });

    // Observe the entire body for changes in the DOM
    this.observer.observe(document.body, {
      childList: true,
      subtree: true, // Important to observe changes in subtrees
    });

    // Perform an initial scan of the DOM for all existing elements, including nested ones
    this.scanAndProcessAllAppComponents(document.body);
  }

  private scanAndProcessAllAppComponents(element: HTMLElement) {
    // Recursive function to scan all "app-xxxx" elements
    if (element.tagName.toLowerCase().startsWith('app-')) {
      this.processElement(element); // Process the element if it is an "app-xxxx"
    }

    // Recursively scan all children of this element
    Array.from(element.children).forEach((child) => {
      this.scanAndProcessAllAppComponents(child as HTMLElement);
    });
  }

  private processElement(element: HTMLElement) {
    // Recursively scan elements and their children
    const tagName = element.tagName.toLowerCase();

    // Exclude 'app-root' component from processing
    if (tagName === 'app-root') {
      return;
    }

    // Apply style if the element is an "app-xxxx" component
    if (tagName.startsWith('app-')) {
      console.log('Processing component:', tagName); // Debugging log for detected components
      // Add a small delay to ensure the component is fully loaded before processing
      setTimeout(() => this.applyStyleToFirstChild(element, tagName), 100);
    }

    // Recursively scan all children of this element
    Array.from(element.children).forEach((child) => {
      this.processElement(child as HTMLElement);
    });
  }

  private applyStyleToFirstChild(element: HTMLElement, tagName: string) {
    const componentName = this.convertTagNameToComponentName(tagName);
    const firstChild = element.firstElementChild as HTMLElement;

    if (!firstChild) {
      return;
    }

    // Save the original styles of the component
    this.saveOriginalStyles(firstChild);

    // Show a red outline immediately on mouse enter
    this.renderer.listen(firstChild, 'mouseenter', () => {
      this.renderer.setStyle(firstChild, 'outline', '2px solid #ff0000');

      // Set a delay of 1 second to activate the full effect
      if (this.hoverDelayTimeout) {
        clearTimeout(this.hoverDelayTimeout);
      }
      this.hoverDelayTimeout = setTimeout(() => {
        this.activateHoverEffect(firstChild, componentName, tagName);
      }, 1000);
    });

    // Only remove timeouts if the mouse leaves before the delay
    this.renderer.listen(firstChild, 'mouseleave', () => {
      if (this.hoverDelayTimeout) {
        clearTimeout(this.hoverDelayTimeout);
      }
      if (this.activeElement === firstChild) {
        this.resetStyles(firstChild, false); // Do not hide the banner, just reset styles
      } else {
        this.renderer.setStyle(firstChild, 'outline', '0px solid #007bff'); // Return the blue outline
      }
    });
  }

  private activateHoverEffect(element: HTMLElement, componentName: string, selectorName: string) {
    // Deactivate effects for any other active element
    if (this.activeElement && this.activeElement !== element) {
      this.resetStyles(this.activeElement, true);
    }

    this.activeElement = element; // Set the new active element

    // Create an overlay
    const overlay = this.renderer.createElement('div');
    this.renderer.setStyle(overlay, 'position', 'absolute');
    this.renderer.setStyle(overlay, 'top', '0');
    this.renderer.setStyle(overlay, 'left', '0');
    this.renderer.setStyle(overlay, 'width', '100%');
    this.renderer.setStyle(overlay, 'height', '100%');
    this.renderer.setStyle(overlay, 'background-color', 'rgba(0, 0, 0, 0.5)');
    this.renderer.setStyle(overlay, 'z-index', '999999999999');
    this.renderer.setStyle(element, 'transform', 'scale(1.05)');
    this.renderer.setStyle(overlay, 'pointer-events', 'none');
    this.renderer.setStyle(overlay, 'transition', 'opacity 0.3s');
    this.renderer.setStyle(overlay, 'opacity', '1');

    // Position the overlay over the element
    this.renderer.setStyle(element, 'position', 'relative'); // Ensure the element is positioned
    this.renderer.appendChild(element, overlay);

    // Show the informational banner
    this.showBanner(componentName, selectorName);

    // Set the listener to remove the overlay when the mouse leaves the element
    const mouseLeaveListener = this.renderer.listen(element, 'mouseleave', () => {
      this.renderer.removeChild(element, overlay); // Remove the overlay
      this.resetStyles(element, false); // Reset styles without hiding the banner
    });

    // Set a timer to restore the initial state after 5 seconds
    this.startHideBannerTimer(() => {
      mouseLeaveListener(); // Remove the listener when the timer expires
      this.renderer.removeChild(element, overlay); // Remove the overlay if still present
      this.resetStyles(element, true); // Restore styles and hide the banner
    });
  }

  private resetStyles(element: HTMLElement, hideBanner: boolean) {
    // Restore the original styles of the component
    if (this.originalStyles.has(element)) {
      const original = this.originalStyles.get(element)!;
      Object.keys(original).forEach((styleName) => {
        this.renderer.setStyle(element, styleName, original[styleName]);
      });
    }
    this.originalStyles.delete(element);
    this.activeElement = null; // No active element

    if (hideBanner) {
      this.hideBanner(); // Hide the banner if specified
    }
  }

  private saveOriginalStyles(element: HTMLElement) {
    const computedStyles = window.getComputedStyle(element);
    const savedStyles: { [key: string]: string } = {
      outline: computedStyles.outline,
      outlineOffset: computedStyles.outlineOffset,
      backgroundColor: computedStyles.backgroundColor,
      zIndex: computedStyles.zIndex,
      transform: computedStyles.transform,
    };
    this.originalStyles.set(element, savedStyles);
  }

  private showBanner(componentName: string, selectorName: string) {
    // Remove the existing banner if any
    if (this.banner) {
      this.renderer.removeChild(document.body, this.banner);
    }

    // Create the banner element
    this.banner = this.renderer.createElement('div');
    this.renderer.setStyle(this.banner, 'position', 'fixed');
    this.renderer.setStyle(this.banner, 'bottom', '0');
    this.renderer.setStyle(this.banner, 'left', '0');
    this.renderer.setStyle(this.banner, 'width', '100%');
    this.renderer.setStyle(this.banner, 'height', '50px');
    this.renderer.setStyle(this.banner, 'background-color', '#000');
    this.renderer.setStyle(this.banner, 'color', '#fff');
    this.renderer.setStyle(this.banner, 'display', 'flex');
    this.renderer.setStyle(this.banner, 'align-items', 'center');
    this.renderer.setStyle(this.banner, 'padding', '0 20px');
    this.renderer.setStyle(this.banner, 'z-index', '9999999999999999999999');
    this.renderer.setStyle(this.banner, 'font-size', '16px');
    this.renderer.setStyle(this.banner, 'justify-content', 'space-between');

    // Create the circular countdown
    const countdownCircle = this.renderer.createElement('div');
    this.renderer.setStyle(countdownCircle, 'width', '30px');
    this.renderer.setStyle(countdownCircle, 'height', '30px');
    this.renderer.setStyle(countdownCircle, 'border-radius', '50%');
    this.renderer.setStyle(countdownCircle, 'background-color', '#fff');
    this.renderer.setStyle(countdownCircle, 'color', '#000');
    this.renderer.setStyle(countdownCircle, 'display', 'flex');
    this.renderer.setStyle(countdownCircle, 'align-items', 'center');
    this.renderer.setStyle(countdownCircle, 'justify-content', 'center');
    this.renderer.setStyle(countdownCircle, 'margin-right', '10px');
    this.renderer.setProperty(countdownCircle, 'innerText', this.countdown.toString());

    // Update the countdown every second
    this.countdown = 5;
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    this.countdownInterval = setInterval(() => {
      this.countdown--;
      this.renderer.setProperty(countdownCircle, 'innerText', this.countdown.toString());
      if (this.countdown <= 0) {
        clearInterval(this.countdownInterval);
      }
    }, 1000);

    // Left text container
    const leftTextContainer = this.renderer.createElement('div');
    this.renderer.setStyle(leftTextContainer, 'display', 'flex');
    this.renderer.setStyle(leftTextContainer, 'align-items', 'center');

    // Text with the component name and selector
    const text = this.renderer.createText(` ${componentName} - ${selectorName}`);
    this.renderer.appendChild(leftTextContainer, countdownCircle);
    this.renderer.appendChild(leftTextContainer, text);

    // "Click here" button with rounded edges
    const button = this.renderer.createElement('button');
    this.renderer.setStyle(button, 'padding', '5px 15px');
    this.renderer.setStyle(button, 'background', '#007bff');
    this.renderer.setStyle(button, 'color', '#fff');
    this.renderer.setStyle(button, 'border', 'none');
    this.renderer.setStyle(button, 'border-radius', '15px');
    this.renderer.setStyle(button, 'cursor', 'pointer');
    this.renderer.setProperty(button, 'innerText', 'Click here');

    // Open the documentation URL on button click
    this.renderer.listen(button, 'click', () => {
      const docUrl = `${this.compodocBaseUrl}/components/${componentName}.html`;
      console.log(`Navigating to: ${docUrl}`);
      window.open(docUrl, '_blank');
    });

    this.renderer.appendChild(this.banner, leftTextContainer);
    this.renderer.appendChild(this.banner, button);
    this.renderer.appendChild(document.body, this.banner);

    // Clear the current timeout if it exists to keep the banner visible during hover
    if (this.bannerTimeout) {
      clearTimeout(this.bannerTimeout);
    }
  }

  private startHideBannerTimer(callback: () => void) {
    // Start a timer to hide the banner
    if (this.bannerTimeout) {
      clearTimeout(this.bannerTimeout);
    }
    this.bannerTimeout = setTimeout(() => {
      this.hideBanner();
      callback(); // Restore styles after the banner disappears
    }, 5000); // Hide the banner after 5 seconds
  }

  private hideBanner() {
    // Hide the banner if it exists
    if (this.banner) {
      this.renderer.removeChild(document.body, this.banner);
      this.banner = null;
    }
  }

  private convertTagNameToComponentName(tagName: string): string {
    // Convert tag name to component name format used by Compodoc
    const nameWithoutPrefix = tagName.replace('app-', '');
    const componentName =
      nameWithoutPrefix
        .split('-')
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join('') + 'Component';

    return componentName;
  }
}
